<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useAuth } from '@/composables/useAuth'
import { useAccount } from '@/composables/useAccount'
import { useChat } from '@/composables/useChat'

const { userName, userEmail, logout } = useAuth()
const { credit, fetchCredit } = useAccount()
const { totalCost } = useChat()

const dropdownOpen = ref(false)
const containerRef = ref<HTMLElement | null>(null)

// Remaining account credit, rendered like the project's "credits consumed"
// badge: truncated to a whole number and without the currency symbol.
//
// Hidden entirely when there is no meaningful balance to show: internal /
// Foresight clients are gate-exempt (unlimited) and have no number to track,
// and unavailable balances (billing off / Vision down) shouldn't clutter the
// header.
const availableCredits = computed<string | null>(() => {
  const c = credit.value
  if (!c || c.unlimited) return null
  if (c.available && typeof c.remaining === 'number') return String(Math.trunc(c.remaining))
  return null
})

onMounted(() => {
  void fetchCredit()
})

// Refresh the remaining credit whenever the project's consumed credits change,
// so the header balance stays in sync with usage.
watch(totalCost, () => {
  void fetchCredit()
})

function getInitials(name: string): string {
  return name
    .split(' ')
    .map(n => n[0] || '')
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

function handleOutsideClick(e: MouseEvent) {
  if (!containerRef.value?.contains(e.target as Node)) {
    dropdownOpen.value = false
  }
}

watch(dropdownOpen, (open) => {
  if (open) document.addEventListener('click', handleOutsideClick)
  else document.removeEventListener('click', handleOutsideClick)
})

async function handleLogout() {
  dropdownOpen.value = false
  await logout()
}
</script>

<template>
  <header class="flex items-center px-4 h-14 shrink-0 bg-white border-b border-neutral-200 text-neutral-800 z-10">
    <div>
      <img src="/horizontal-logo.png" alt="Foresight" class="ml-1 h-[50px] max-w-[185px] object-contain" />
      <div class="-mt-4 ml-12  text-[11px] text-neutral-500 select-none">Activism & Protest Intelligence Smart Assist</div>
    </div>

    <div ref="containerRef" class="ml-auto relative flex items-center gap-3">
      <span
        v-if="availableCredits !== null"
        class="text-[11px] font-medium text-neutral-500 whitespace-nowrap"
      >
        available credits: <span class="font-semibold text-neutral-700">{{ availableCredits }}</span>
      </span>

      <button
        class="w-8 h-8 rounded-full bg-neutral-100 hover:bg-neutral-200 text-neutral-700 flex items-center justify-center text-sm font-semibold transition-colors"
        :title="userName"
        @click="dropdownOpen = !dropdownOpen"
      >
        {{ getInitials(userName || '?') }}
      </button>

      <div
        v-if="dropdownOpen"
        class="absolute right-0 top-10 w-52 bg-white rounded-md shadow-lg border border-neutral-200 py-1 z-50 text-neutral-800"
      >
        <div class="px-4 py-2.5 border-b border-neutral-100">
          <p class="text-sm font-semibold text-neutral-800 truncate">{{ userName || 'User' }}</p>
          <p v-if="userEmail" class="text-xs text-neutral-500 truncate mt-0.5">{{ userEmail }}</p>
        </div>
        <button
          class="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-neutral-50 transition-colors"
          @click="handleLogout"
        >
          Sign out
        </button>
      </div>
    </div>
  </header>
</template>
